'use client';

import { useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import styles from './PowerplugSection.module.css';

/* Hand+phone mockup PNG (transparent screen cutout) — same asset that
   Ring PRO's AppSection uses, so the visual treatment matches. The
   mobile variant is a phone-only mockup (no hand) for tighter framing
   on small screens. */
const POWERPLUG_HAND_PHONE =
  'web_v2/homepage-v3/app-section/hand-phone-mockup.png';

/* Screen content. When POWERPLUG_SCREEN_VIDEO is set the component
   renders a <video> inside the phone screen and auto-plays it via
   IntersectionObserver as soon as the section scrolls into view. The
   video loops continuously (loop muted playsInline). When unset,
   POWERPLUG_SCREEN_IMAGE renders as a static fallback. */
const POWERPLUG_SCREEN_IMAGE =
  'web_v2/photon/landing-page/powerplug-screen.png';
const POWERPLUG_SCREEN_VIDEO = getCompressedAssetUrl(
  '/web_v2/photon/landing-page/powerplug-screen-v4.mp4',
);

/**
 * Photon Powerplug — hand-holding-phone mockup with the Photon app
 * playing on the screen (image placeholder for now, video supported).
 * Pattern mirrors Ring PRO's AppSection: the hand+phone PNG sits ON TOP
 * (z-index 2), the screen content sits BEHIND (z-index 1) and shows
 * through a transparent cutout in the PNG. No precise rounded-corner
 * masking on the video needed.
 */
const PowerplugSection = () => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const t = useTranslations('photon');

  /* Auto-play the screen video when the section enters the viewport,
     pause when it exits. Saves bandwidth/CPU when offscreen and gives
     the user the "starts when you arrive" experience they asked for.
     Muted + playsInline lets autoplay succeed across all browsers
     including iOS Safari. */
  useEffect(() => {
    if (!POWERPLUG_SCREEN_VIDEO) return;
    const section = sectionRef.current;
    const video = videoRef.current;
    if (!section || !video) return;
    if (typeof IntersectionObserver === 'undefined') {
      void video.play().catch(() => undefined);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            void video.play().catch(() => undefined);
          } else {
            video.pause();
          }
        }
      },
      { threshold: 0.25 },
    );
    io.observe(section);
    return () => io.disconnect();
  }, []);

  return (
    <section className={styles.section} ref={sectionRef}>
      <div className={styles.header}>
        <p className={styles.eyebrow}>
          {t('photon.powerplugSection.eyebrow.protocolPowerplug')}
        </p>
        <h2 className={styles.heading}>
          {t('photon.powerplugSection.heading.targetedRecovery')}
        </h2>
        <p className={styles.description}>
          {t('photon.powerplugSection.description.tellsYouArea')}
        </p>
      </div>

      <div className={styles.phoneWrapper} aria-hidden="true">
        <div className={styles.phoneScreen}>
          {POWERPLUG_SCREEN_VIDEO ? (
            <video
              className={styles.screenVideo}
              ref={videoRef}
              src={POWERPLUG_SCREEN_VIDEO}
              loop
              muted
              playsInline
              preload="metadata"
            />
          ) : (
            <CustomImage
              className={styles.screenImageEl}
              src={POWERPLUG_SCREEN_IMAGE}
              alt=""
              width={2340}
              height={4898}
              sizes="(max-width: 768px) 50vw, 320px"
              priority={false}
            />
          )}
        </div>
        <CustomImage
          className={styles.phoneImage}
          src={POWERPLUG_HAND_PHONE}
          width={5000}
          height={4444}
          alt=""
        />
      </div>
    </section>
  );
};

export default PowerplugSection;
